import { DailySummaryCalculator, type NutritionSummary } from "../domain/calculators.js";
import type { ISODate, Meal, MealItem, MealType, NutritionTarget, WaterEntry } from "../domain/model.js";
import type { MealRepository } from "./meal-repository.js";
import type { WaterRepository } from "./water-repository.js";
import { targetForDate } from "./goal-planner.js";
import { ACTIVITY_EVENT, budgetForActivityDate } from "./activity.js";

export interface DashboardMacro {
  consumed: number;
  goal: number | null;
  progress: number;
}

export interface DashboardMeal extends NutritionSummary {
  type: MealType;
  itemCount: number;
}

export interface HomeDashboardState {
  nutritionIncomplete?: boolean;
  date: ISODate;
  calorieGoal: number | null;
  eatenCalories: number;
  burnedCalories: number;
  activityCalorieBonus: number;
  remainingCalories: number;
  calorieProgress: number;
  macros: {
    protein: DashboardMacro;
    fat: DashboardMacro;
    carbs: DashboardMacro;
  };
  water: { consumed: number; goal: number; milliliters: number };
  meals: Record<MealType, DashboardMeal>;
  streakDays: number;
}

export interface DashboardSource {
  date: ISODate;
  target: NutritionTarget | null;
  mealItems: readonly MealItem[];
  meals?: readonly Meal[];
  waterEntries: readonly WaterEntry[];
  burnedCalories?: number;
  activityCalorieBonus?: number;
  streakDays?: number;
}

const emptyMeal = (type: MealType): DashboardMeal => ({ type, itemCount: 0, calories: 0, protein: 0, fat: 0, carbs: 0 });

function progress(consumed: number, goal: number | null): number {
  if (!goal || goal <= 0) return 0;
  return Math.max(0, consumed / goal);
}

export function buildHomeDashboardState(source: DashboardSource): HomeDashboardState {
  const summary = DailySummaryCalculator.calculate(source.mealItems, source.waterEntries, source.target);
  const burnedCalories = source.burnedCalories ?? 0;
  const activityCalorieBonus = source.activityCalorieBonus ?? burnedCalories;
  const meals: HomeDashboardState["meals"] = {
    breakfast: emptyMeal("breakfast"),
    lunch: emptyMeal("lunch"),
    dinner: emptyMeal("dinner"),
    snack: emptyMeal("snack"),
  };
  for (const meal of source.meals ?? []) {
    if (meal.deletedAt) continue;
    const items = source.mealItems.filter(item => item.mealId === meal.id && !item.deletedAt);
    const summary = DailySummaryCalculator.calculate(items, [], null);
    const current = meals[meal.type];
    meals[meal.type] = { type: meal.type, itemCount: current.itemCount + items.length, calories: current.calories + summary.calories, protein: current.protein + summary.protein, fat: current.fat + summary.fat, carbs: current.carbs + summary.carbs };
  }

  return {
    date: source.date,
    ...(source.mealItems.some(item => !item.deletedAt && item.nutritionIncomplete) ? { nutritionIncomplete: true } : {}),
    calorieGoal: source.target?.dailyCalories ?? null,
    eatenCalories: summary.calories,
    burnedCalories,
    activityCalorieBonus,
    remainingCalories: source.target ? source.target.dailyCalories + activityCalorieBonus - summary.calories : 0,
    calorieProgress: progress(summary.calories, source.target ? source.target.dailyCalories + activityCalorieBonus : null),
    macros: {
      protein: { consumed: summary.protein, goal: source.target?.proteinGrams ?? null, progress: progress(summary.protein, source.target?.proteinGrams ?? null) },
      fat: { consumed: summary.fat, goal: source.target?.fatGrams ?? null, progress: progress(summary.fat, source.target?.fatGrams ?? null) },
      carbs: { consumed: summary.carbs, goal: source.target?.carbsGrams ?? null, progress: progress(summary.carbs, source.target?.carbsGrams ?? null) },
    },
    water: { consumed: summary.waterGlasses, goal: source.target?.waterGlasses ?? 8, milliliters: source.waterEntries.reduce((sum, entry) => sum + (entry.milliliters ?? entry.glasses * 250), 0) },
    meals,
    streakDays: source.streakDays ?? 0,
  };
}

export function emptyHomeDashboardState(date: ISODate): HomeDashboardState {
  return buildHomeDashboardState({ date, target: null, mealItems: [], waterEntries: [] });
}

export class HomeDashboardRepository {
  constructor(private readonly waterRepository: WaterRepository, private readonly mealRepository?: MealRepository) {}

  async get(date: ISODate): Promise<HomeDashboardState> {
    const waterMilliliters = await this.waterRepository.getTotalMilliliters(date);
    const now = new Date().toISOString();
    const waterEntries: WaterEntry[] = waterMilliliters === 0 ? [] : [{
      id: `dashboard-water-${date}`,
      dateTime: `${date}T12:00:00`,
      glasses: waterMilliliters / 250,
      milliliters: waterMilliliters,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      syncStatus: "synced",
    }];
    const mealDay = await this.mealRepository?.getDay(date) ?? { meals: [], items: [] };
    const activity = budgetForActivityDate(date);
    return buildHomeDashboardState({ date, target: targetForDate(date), meals: mealDay.meals, mealItems: mealDay.items, waterEntries, burnedCalories: activity.burnedCalories, activityCalorieBonus: activity.additionalCalories });
  }

  addWaterGlass(date: ISODate, glassMilliliters: number): Promise<void> {
    return this.waterRepository.addMilliliters(date, glassMilliliters);
  }

  setWater(date: ISODate, milliliters: number): Promise<void> {
    return this.waterRepository.setTotalMilliliters(date, milliliters);
  }

  subscribe(date: ISODate, listener: () => void): () => void {
    const unsubscribeWater = this.waterRepository.subscribe(date, listener);
    const unsubscribeMeals = this.mealRepository?.subscribe(date, listener) ?? (() => {});
    const goalListener = () => listener();
    window.addEventListener("yumnote:goal-plan", goalListener);
    window.addEventListener(ACTIVITY_EVENT, goalListener);
    window.addEventListener("storage", goalListener);
    return () => { unsubscribeWater(); unsubscribeMeals(); window.removeEventListener("yumnote:goal-plan", goalListener); window.removeEventListener(ACTIVITY_EVENT, goalListener); window.removeEventListener("storage", goalListener); };
  }
}
